import { useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import PunchListActionButtons from '../../../../src/components/ActionButtons/PunchListActionButtons';
import Comments from '../../../components/CoreComponents/Comments/Comments.tsx';
import DetailGrid from '../../../components/CoreComponents/Details/DetailsGrid.tsx';
import SectionHeader from "../../../components/CoreComponents/Details/SectionHeader.tsx";
import { RootState } from '../../../store/store';
import PunchListDetails from "./PunchListDetails.tsx";
import PunchListImages from "./PunchListImages.tsx";
import Changelogs from '../../../components/CoreComponents/Changelog/Changelogs.tsx';
import { setModal } from "../../../store/Utilities.ts";
import { buildChecklistDetailRows } from './checklistDetailsHelpers.ts';
import { FaArrowRight, FaArrowLeft } from "react-icons/fa";

export default function DetailsPage() {
    const { punchListId } = useParams();
    const location = useLocation();
    const filteredPunchListIDs = location.state as string[]
    const allPunchList = useSelector((state: RootState) => state.PunchList.ProjectPunchLists);
    const currentDisplayedPunchList = allPunchList.find((item) => item.punchListUuid === punchListId)
    const relatedChecklistDetails = useSelector((state: RootState) => state.RelatedChecklistDetails.relatedChecklistDetails);
    const relatedLoadedChecklistDetails = useSelector((state: RootState) => state.SequenceObject.ChecklistsObject);
    const { DrilldownMappingsAPIResponse } = useSelector((state: RootState) => state.SequenceObject);

    const checklistUuid = currentDisplayedPunchList?.ctUuid;
    const checklistDetails = useMemo(
        () => buildChecklistDetailRows({
            checklistUuid,
            relatedLoadedChecklistDetails,
            relatedChecklistDetails,
            mappings: DrilldownMappingsAPIResponse,
            adhocSequenceCa: currentDisplayedPunchList?.adhocSequenceCa,
            adhocUnitDevice: currentDisplayedPunchList?.adhocUnitDevice,
            disciplineId: currentDisplayedPunchList?.creatorDisciplineId,
        }),
        [checklistUuid, relatedLoadedChecklistDetails, relatedChecklistDetails, DrilldownMappingsAPIResponse, currentDisplayedPunchList?.adhocSequenceCa, currentDisplayedPunchList?.adhocUnitDevice, currentDisplayedPunchList?.creatorDisciplineId],
    );

    const generalDetails = useMemo(() => {
        const details = [
            { label: "Checklist UUID", value: currentDisplayedPunchList?.ctUuid?.toUpperCase() },
            { label: "Punch List UUID", value: currentDisplayedPunchList?.punchListUuid?.toUpperCase() },
            { label: "Created By", value: currentDisplayedPunchList?.identifiedBy },
        ];
        return details.filter(detail => detail.value != null);
    }, [currentDisplayedPunchList]);

    const dispatch = useDispatch();
    const navigate = useNavigate();
    const addEditPhotoModal = () => {
        if (detailsEditEnabled) {
            navigate(`/PunchList/DetailsPage/${currentDisplayedPunchList?.punchListUuid}/Image`);
        }
        dispatch(setModal({
            Chilren: () =>
                <div>
                    Proceeding will discard any changes that haven't been saved. Do you wish to continue?<br />
                </div>,
            onConfirm: () => {
                navigate(`/PunchList/DetailsPage/${currentDisplayedPunchList?.punchListUuid}/Image`)
            },
            open: true
        }));
    }

    const [detailsEditEnabled, setDetailsEditEnabled] = useState(false);
    const isArrowsNavigationsEnabled = !!filteredPunchListIDs || filteredPunchListIDs?.length !== 0

    const handleClick = (direction: 'next' | 'previous') => {
        if (!currentDisplayedPunchList || filteredPunchListIDs?.length === 0) return;

        const currentPunchListIDIndex = filteredPunchListIDs?.findIndex((id) => id === currentDisplayedPunchList.punchListUuid);
        if (currentPunchListIDIndex === -1) return;

        let newIndex = null;
        if (direction === 'next') newIndex = (currentPunchListIDIndex + 1) % filteredPunchListIDs?.length;
        else newIndex = (currentPunchListIDIndex - 1 + filteredPunchListIDs?.length) % filteredPunchListIDs?.length;

        const newPunchListId = filteredPunchListIDs?.[newIndex];
        const newPunchList = allPunchList.find(punchlist => punchlist?.punchListUuid === newPunchListId)
        navigate(`/PunchList/DetailsPage/${newPunchList.punchListUuid}`, { state: filteredPunchListIDs })
    };

    // Determine if the current element is the first or last in the array
    const isFirstElement = filteredPunchListIDs?.findIndex((id) => id === currentDisplayedPunchList?.punchListUuid) === 0;
    const isLastElement = filteredPunchListIDs?.findIndex((id) => id === currentDisplayedPunchList?.punchListUuid) === filteredPunchListIDs?.length - 1;
    const getCurrentArrowStyles = (isActive: boolean) => isActive ? "opacity-50 pointer-events-none cursor-no-drop" : "opacity-100 cursor-pointer"

    return (


        <>
            <PunchListActionButtons item={currentDisplayedPunchList} addEditPhotoModal={addEditPhotoModal} />
            <div className='max-w-4xl ml-10 relative'>
                {isArrowsNavigationsEnabled && <div onClick={() => handleClick("previous")}
                    className={`fixed left-[2rem] top-1/2 transform -translate-y-1/2 ${getCurrentArrowStyles(isFirstElement)}`}>
                    <FaArrowLeft />
                </div>}
                <PunchListDetails
                    item={currentDisplayedPunchList}
                    detailsEditEnabled={detailsEditEnabled}
                    setDetailsEditEnabled={setDetailsEditEnabled}
                />

                <hr className="border-t mt-8 mb-8" />

                <PunchListImages item={currentDisplayedPunchList} addEditPhotoModal={addEditPhotoModal} />

                <hr className="border-t mt-8 mb-8" />

                <div className="flex flex-col mb-4">
                    <SectionHeader title="Checklist Details" />
                </div>

                <DetailGrid details={checklistDetails} />

                <hr className="border-t mt-8 mb-8" />

                <div className="flex flex-col mt-16 mb-4">
                    <Comments item={currentDisplayedPunchList} />
                </div>

                <div className="flex flex-col mt-16 mb-4">
                    <Changelogs item={currentDisplayedPunchList} />
                </div>

                <div className="flex flex-col mt-16 mb-4">
                    <SectionHeader title="General Details" />
                </div>

                <DetailGrid details={generalDetails} />
                {isArrowsNavigationsEnabled && <div
                    onClick={() => handleClick("next")}
                    className={`fixed right-[5rem] top-1/2 transform -translate-y-1/2 ${getCurrentArrowStyles(isLastElement)}`}>
                    <FaArrowRight />
                </div>}
            </div>
        </>
    );
}

